import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../admin/firebase/config';
import logger from '../utils/logger';

const useMenuItems = () => {
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to menu items from Firebase in real-time
    const q = query(collection(db, 'menu_items'), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Only include items that are explicitly marked as available
      const availableItems = items.filter(item => item.isAvailable === true);

      // Log menu statistics in development mode
      const unavailableItems = items.filter(item => item.isAvailable !== true);
      if (unavailableItems.length > 0) {
        logger.info(`📊 Menu: ${items.length} total, ${availableItems.length} available, ${unavailableItems.length} filtered`);
      }

      setMenuItems(availableItems);
      setLoading(false);
    }, (error) => {
      logger.error('Error fetching menu items:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Group menu items by category for the customer view
  const groupedMenu = menuItems.reduce((acc, item) => {
    const category = item.category || 'Uncategorized';

    if (!acc[category]) {
      acc[category] = {
        category: category,
        sections: [{
          name: category,
          subsections: [{
            veg: { items: [] },
            nonVeg: { items: [] }
          }]
        }]
      };
    }

    // Add item to appropriate veg/non-veg array with null safety checks
    const subsection = acc[category].sections[0].subsections[0];

    // Helper to safely get price from sizes array
    const getPrice = (sizes, index) => {
      return sizes && sizes.length > index && sizes[index] && typeof sizes[index].price === 'number'
        ? sizes[index].price
        : 0;
    };

    const itemData = {
      name: item.name || 'Unnamed Item',
      price: getPrice(item.sizes, 0),
      mrp: getPrice(item.sizes, 1) || getPrice(item.sizes, 0),
      description: item.description || ''
    };

    if (item.isVeg) {
      subsection.veg.items.push(itemData);
    } else {
      subsection.nonVeg.items.push(itemData);
    }

    return acc;
  }, {});

  // Convert to array format matching menu-data.json structure
  const categories = Object.values(groupedMenu);

  return {
    menuData: {
      restaurantName: 'Odisha Zaika',
      totalItems: menuItems.length,
      categories: categories
    },
    loading
  };
};

export default useMenuItems;
